/**
 * 通用底部导航（4 Tab）：首页 / 任务 / 消息 / 我的
 * 视觉对齐设计稿：58px 毛玻璃白底 + 顶部细线，线性图标；激活态品牌蓝 + 实心图标。
 * 消息 Tab 带未读角标（GET /notification/unread-count）。
 *
 * 四 Tab 各指向独立路径：
 * - 首页：今日作业工作台（/mobile/home）
 * - 任务：全量任务中心（/mobile/tasks，含待处理/进行中/已完成）
 * - 消息：业务通知中心（/mobile/messages）
 * - 我的：个人工作档案（/mobile/me）
 */
import { Box, ButtonBase } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PersonIcon from '@mui/icons-material/Person';
import { getUnreadCount } from '@/api/notification';
import { CountBadge } from './ui';
import { T } from '@/theme/tokens';

interface TabDef {
  label: string;
  icon: React.ReactNode;
  iconOn: React.ReactNode;
  /** 点击导航目标 */
  nav: string;
  /** 当前路径是否满足该 Tab 高亮 */
  active: (path: string) => boolean;
  /** 是否展示未读角标 */
  withUnread?: boolean;
}

const tabs: TabDef[] = [
  {
    label: '首页',
    icon: <HomeOutlinedIcon />,
    iconOn: <HomeIcon />,
    nav: '/mobile/home',
    active: (p) => p === '/mobile/home',
  },
  {
    label: '任务',
    icon: <AssignmentOutlinedIcon />,
    iconOn: <AssignmentIcon />,
    nav: '/mobile/tasks',
    active: (p) =>
      p === '/mobile/tasks' ||
      p.startsWith('/mobile/task/') ||
      p.startsWith('/mobile/load/'),
  },
  {
    label: '消息',
    icon: <ChatBubbleOutlineIcon />,
    iconOn: <ChatBubbleIcon />,
    nav: '/mobile/messages',
    active: (p) => p === '/mobile/messages',
    withUnread: true,
  },
  {
    label: '我的',
    icon: <PersonOutlineIcon />,
    iconOn: <PersonIcon />,
    nav: '/mobile/me',
    active: (p) => p === '/mobile/me',
  },
];

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  // 未读角标轮询（60s），登录态下有效；失败静默
  useEffect(() => {
    let alive = true;
    const fetchUnread = () => {
      getUnreadCount()
        .then((n) => {
          if (alive) setUnread(typeof n === 'number' ? n : 0);
        })
        .catch(() => {
          if (alive) setUnread(0);
        });
    };
    fetchUnread();
    const timer = setInterval(fetchUnread, 60000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const activeIdx = Math.max(0, tabs.findIndex((t) => t.active(location.pathname)));

  return (
    <Box
      component="nav"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: { xs: '100%', sm: 480 },
        zIndex: 1200,
        display: 'flex',
        height: 'calc(58px + env(safe-area-inset-bottom))',
        pb: 'calc(4px + env(safe-area-inset-bottom))',
        bgcolor: 'rgba(255,255,255,.94)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTop: `1px solid ${T.line}`,
      }}
    >
      {tabs.map((tab, i) => {
        const on = i === activeIdx;
        return (
          <ButtonBase
            key={tab.label}
            aria-label={tab.label}
            aria-current={on ? 'page' : undefined}
            onClick={() => navigate(tab.nav)}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              position: 'relative',
              color: on ? T.brand : T.ink3,
              fontFamily: 'inherit',
              '& .MuiSvgIcon-root': { fontSize: 23 },
            }}
          >
            {on ? tab.iconOn : tab.icon}
            <Box component="span" sx={{ fontSize: '10.5px', fontWeight: 600, lineHeight: 1.2 }}>
              {tab.label}
            </Box>
            {tab.withUnread && unread > 0 && (
              <CountBadge n={unread} sx={{ top: 4, right: 'calc(50% - 22px)' }} />
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
